import { FC } from "react";
import { FieldInputProps } from "react-final-form";

import { IPickerListItem, Picker } from "@livechat/design-system-react-components";

import { TicketStatus, TicketStatusFormattedValues } from "interfaces/entities/ticket";
import { PickerContainerWithLabel } from "routes/automations/shared/components/PickerContainerWithLabel";
import { IRuleTriggerStatusChanged } from "store/entities/rules/interfaces";

import { TRIGGER_ANY_OPTION_KEY, changeTriggerValues } from "../utils";

interface IProps {
  input: FieldInputProps<IRuleTriggerStatusChanged>;
}

export const StatusChangedTrigger: FC<IProps> = ({ input }) => {
  const { oldStatusIn, newStatusIn } = input.value;

  const statusOptions = [ANY_OPTION, ...Object.values(TicketStatus).map(createStatusOption)];

  const selectedFromStatusOptions = oldStatusIn ? oldStatusIn.map(createStatusOption) : [ANY_OPTION];

  const selectedToStatusOptions = newStatusIn ? newStatusIn.map(createStatusOption) : [ANY_OPTION];

  const handleSelectStatus =
    (property: "oldStatusIn" | "newStatusIn", previousSelected: IPickerListItem[]) =>
    (selectedOptions: IPickerListItem[] | null): void => {
      if (!selectedOptions) {
        changeTriggerValues(input, { [property]: [] });
        return;
      }

      const currentOptionKeys = selectedOptions.map(opt => opt.key);
      const previousOptionKeys = previousSelected.map(opt => opt.key);

      const isAnySelected = currentOptionKeys.includes(TRIGGER_ANY_OPTION_KEY);
      const isAnyPreviouslySelected = previousOptionKeys.includes(TRIGGER_ANY_OPTION_KEY);

      let statusIn: TicketStatus[] | undefined;

      if (isAnySelected) {
        statusIn = isAnyPreviouslySelected
          ? currentOptionKeys.filter(key => key !== TRIGGER_ANY_OPTION_KEY).map(key => key as TicketStatus)
          : undefined;
      } else {
        statusIn = currentOptionKeys.map(key => key as TicketStatus);
      }

      changeTriggerValues(input, { [property]: statusIn });
    };

  const handleSelectFromStatus = handleSelectStatus("oldStatusIn", selectedFromStatusOptions);

  const handleSelectToStatus = handleSelectStatus("newStatusIn", selectedToStatusOptions);

  return (
    <>
      <PickerContainerWithLabel label="from">
        <Picker
          type="multi"
          options={statusOptions}
          selected={selectedFromStatusOptions}
          onSelect={handleSelectFromStatus}
          searchDisabled
          placeholder="Select one status or more..."
        />
      </PickerContainerWithLabel>
      <PickerContainerWithLabel label="to">
        <Picker
          type="multi"
          options={statusOptions}
          selected={selectedToStatusOptions}
          onSelect={handleSelectToStatus}
          searchDisabled
          placeholder="Select one status or more..."
        />
      </PickerContainerWithLabel>
    </>
  );
};

const ANY_OPTION: IPickerListItem = {
  key: TRIGGER_ANY_OPTION_KEY,
  name: "Any status",
};

const createStatusOption = (status: TicketStatus): IPickerListItem => ({
  key: status,
  name: TicketStatusFormattedValues[status],
});
